// Reference list of HTTP status codes drawn from RFC 9110 plus widely-used
// extensions (WebDAV — RFC 4918, additional client/server errors — RFCs 6585,
// 7538, 7540, 7725, 8470). Status names are kept verbatim in English because
// they are the canonical protocol identifiers; localised UI chrome lives in
// the translation files.

export const HTTP_STATUS_CLASSES = ["1xx", "2xx", "3xx", "4xx", "5xx"];

export const HTTP_STATUS_CODES = [
  // 1xx — Informational
  {
    code: 100,
    name: "Continue",
    category: "1xx",
    summary:
      "Initial part of a request was received and the client should continue with the rest.",
    details:
      "Sent by the server when the client has indicated Expect: 100-continue. The client may then send the request body; otherwise the server will reply with the final response without the body being transmitted.",
  },
  {
    code: 101,
    name: "Switching Protocols",
    category: "1xx",
    summary:
      "The server agrees to switch to the protocol requested in the Upgrade header.",
    details:
      "Used to upgrade an HTTP/1.1 connection to a different protocol such as WebSocket or HTTP/2 over cleartext. The response carries an Upgrade header naming the new protocol.",
  },
  {
    code: 102,
    name: "Processing",
    category: "1xx",
    summary:
      "WebDAV — the server has received and is processing a long-running request.",
    details:
      "Defined by RFC 2518 to keep clients from timing out during operations such as PROPFIND or COPY. Deprecated for general use; modern servers prefer 103 Early Hints or no interim response.",
  },
  {
    code: 103,
    name: "Early Hints",
    category: "1xx",
    summary:
      "Server is still preparing the final response but is sending preliminary headers.",
    details:
      "Defined by RFC 8297. Typically used to send Link: rel=preload headers so browsers can start fetching CSS, JS, or fonts before the main response is ready.",
  },

  // 2xx — Success
  {
    code: 200,
    name: "OK",
    category: "2xx",
    summary: "The request succeeded.",
    details:
      "The default success status. The semantics of the response body depend on the request method — for GET it is the resource representation, for POST it is the result of the action.",
  },
  {
    code: 201,
    name: "Created",
    category: "2xx",
    summary: "The request succeeded and a new resource was created.",
    details:
      "Most commonly returned by POST and PUT. The Location header should point to the newly created resource and the body usually contains its representation.",
  },
  {
    code: 202,
    name: "Accepted",
    category: "2xx",
    summary: "The request was accepted for processing but is not yet complete.",
    details:
      "Used for asynchronous workflows. The response should describe how the client can poll or otherwise track the eventual outcome.",
  },
  {
    code: 203,
    name: "Non-Authoritative Information",
    category: "2xx",
    summary:
      "The returned metadata differs from what the origin server would return.",
    details:
      "Indicates that a transforming proxy modified the response. Rarely seen in practice; 200 OK is preferred unless you specifically need to signal the modification.",
  },
  {
    code: 204,
    name: "No Content",
    category: "2xx",
    summary: "The request succeeded but there is no body to return.",
    details:
      "Useful for DELETE and PUT actions where the client does not need a representation back. The server must not send a message body.",
  },
  {
    code: 205,
    name: "Reset Content",
    category: "2xx",
    summary: "The request succeeded; the client should reset the form view.",
    details:
      "Hints to a user agent that displayed the request that it should clear the form so the user can submit a new entry.",
  },
  {
    code: 206,
    name: "Partial Content",
    category: "2xx",
    summary:
      "The server is delivering part of the resource in response to a Range request.",
    details:
      "Used to support resumable downloads and byte-range media streaming. The response carries a Content-Range header describing the returned segment.",
  },
  {
    code: 207,
    name: "Multi-Status",
    category: "2xx",
    summary:
      "WebDAV — the response body conveys multiple independent statuses.",
    details:
      "Defined by RFC 4918. Used for batch WebDAV operations where different sub-resources have different outcomes; the body is an XML multistatus document.",
  },
  {
    code: 208,
    name: "Already Reported",
    category: "2xx",
    summary:
      "WebDAV — members of a binding have already been enumerated in this response.",
    details:
      "Avoids repeating the bindings of the same collection multiple times in a 207 Multi-Status reply.",
  },
  {
    code: 226,
    name: "IM Used",
    category: "2xx",
    summary:
      "The server has fulfilled a GET request and the response is the result of one or more instance-manipulations.",
    details:
      "Defined by RFC 3229 for delta encoding. Almost never used outside that specific protocol extension.",
  },

  // 3xx — Redirection
  {
    code: 300,
    name: "Multiple Choices",
    category: "3xx",
    summary:
      "The request has more than one possible response; the client should pick one.",
    details:
      "Useful for content negotiation when the server cannot decide for the client. The response usually lists candidate resources and their identifiers.",
  },
  {
    code: 301,
    name: "Moved Permanently",
    category: "3xx",
    summary: "The resource has permanently moved to the URL in Location.",
    details:
      "Search engines update their indexes. Browsers and intermediaries are allowed to cache the redirect indefinitely. Use 308 if you need to preserve the request method on redirect.",
  },
  {
    code: 302,
    name: "Found",
    category: "3xx",
    summary:
      "The resource is temporarily at a different URL given by Location.",
    details:
      "Historically clients changed POST to GET when following a 302, even though the spec is ambiguous. Prefer 303 (always GET) or 307 (preserve method) for unambiguous behaviour.",
  },
  {
    code: 303,
    name: "See Other",
    category: "3xx",
    summary:
      "Redirects the client to retrieve the result with a GET to the URL in Location.",
    details:
      "The standard pattern for the POST-Redirect-GET workflow that prevents form re-submission on browser reload.",
  },
  {
    code: 304,
    name: "Not Modified",
    category: "3xx",
    summary:
      "The cached copy is still fresh; the body is not retransmitted.",
    details:
      "Returned in response to a conditional GET that includes If-None-Match or If-Modified-Since. Saves bandwidth without invalidating the cached representation.",
  },
  {
    code: 305,
    name: "Use Proxy",
    category: "3xx",
    summary:
      "Deprecated — the requested resource is only available through a proxy.",
    details:
      "Removed from RFC 9110 because of security concerns. Modern servers should not return this status.",
  },
  {
    code: 307,
    name: "Temporary Redirect",
    category: "3xx",
    summary:
      "Redirect to the URL in Location while preserving the original method and body.",
    details:
      "Like 302 but with a strict guarantee that POST stays POST. Use this when you need to reroute non-GET requests temporarily, for example during a maintenance window.",
  },
  {
    code: 308,
    name: "Permanent Redirect",
    category: "3xx",
    summary:
      "Permanent redirect that preserves the original method and body.",
    details:
      "Like 301 but stricter — a client must not change the request method. Useful for permanent migrations of API endpoints that accept POST or PUT.",
  },

  // 4xx — Client Error
  {
    code: 400,
    name: "Bad Request",
    category: "4xx",
    summary:
      "The server cannot process the request because it is malformed.",
    details:
      "Catch-all for syntactic problems such as malformed JSON or missing required fields. For semantic validation failures consider 422 Unprocessable Content.",
  },
  {
    code: 401,
    name: "Unauthorized",
    category: "4xx",
    summary:
      "Authentication is required and has either not been provided or has failed.",
    details:
      "Despite the name, this is about authentication, not authorization. The response must include a WWW-Authenticate header describing the expected scheme. Use 403 once the user is identified but not allowed.",
  },
  {
    code: 402,
    name: "Payment Required",
    category: "4xx",
    summary: "Reserved for future use; sometimes used for paid APIs.",
    details:
      "Originally intended for digital cash systems. Used today by some APIs to indicate that a request was rejected for billing reasons (quota exceeded, plan upgrade required).",
  },
  {
    code: 403,
    name: "Forbidden",
    category: "4xx",
    summary:
      "The server understood the request but refuses to authorize it.",
    details:
      "Used when the caller is identified but the action is denied — for example accessing another user's resource or hitting a feature flag that is off. Re-authenticating will not help.",
  },
  {
    code: 404,
    name: "Not Found",
    category: "4xx",
    summary: "The requested resource does not exist.",
    details:
      "May also be used to obscure the existence of a forbidden resource. Should be returned for unknown route paths and for IDs that do not match anything in the database.",
  },
  {
    code: 405,
    name: "Method Not Allowed",
    category: "4xx",
    summary:
      "The HTTP method is not supported for this resource.",
    details:
      "The response must include an Allow header listing the methods the resource does support. Often returned by REST APIs when a client uses POST on a read-only collection.",
  },
  {
    code: 406,
    name: "Not Acceptable",
    category: "4xx",
    summary:
      "The server cannot produce a response matching the Accept headers.",
    details:
      "Typically thrown during content negotiation when the client requested a media type, language, or encoding the server does not provide.",
  },
  {
    code: 407,
    name: "Proxy Authentication Required",
    category: "4xx",
    summary:
      "Like 401, but the client must authenticate with a proxy first.",
    details:
      "The Proxy-Authenticate header describes how the client should authenticate. Mostly seen in corporate networks with authenticating egress proxies.",
  },
  {
    code: 408,
    name: "Request Timeout",
    category: "4xx",
    summary:
      "The server timed out waiting for the client to finish the request.",
    details:
      "Connection-level timeout, distinct from 504 (which is about an upstream timeout). Most clients are expected to retry after closing the connection.",
  },
  {
    code: 409,
    name: "Conflict",
    category: "4xx",
    summary:
      "The request conflicts with the current state of the resource.",
    details:
      "Common in optimistic concurrency control (\"the resource was modified since you last read it\") and unique-constraint violations (\"that username is taken\").",
  },
  {
    code: 410,
    name: "Gone",
    category: "4xx",
    summary:
      "The resource was here but has been intentionally removed.",
    details:
      "Stronger than 404 — a sign to caches and search engines that the URL will not return. Useful for retired API endpoints or deleted user content.",
  },
  {
    code: 411,
    name: "Length Required",
    category: "4xx",
    summary:
      "The request is missing a required Content-Length header.",
    details:
      "The client must add Content-Length and resend. Rarely seen with modern clients; HTTP/2 and HTTP/3 do not need Content-Length at all.",
  },
  {
    code: 412,
    name: "Precondition Failed",
    category: "4xx",
    summary:
      "A condition in the request headers (e.g. If-Match) was not met.",
    details:
      "Used in conditional requests. A typical case is updating a resource only if its ETag still matches — a mismatch returns 412 so the client can refetch.",
  },
  {
    code: 413,
    name: "Content Too Large",
    category: "4xx",
    summary:
      "The request body exceeds the server's size limit.",
    details:
      "Previously called \"Payload Too Large\". May include a Retry-After header if the limit is temporary. Often returned by upload endpoints and reverse proxies.",
  },
  {
    code: 414,
    name: "URI Too Long",
    category: "4xx",
    summary: "The request URI is longer than the server is willing to parse.",
    details:
      "Usually a sign that GET parameters should be moved into the body of a POST. Most servers cap URI length around 8 KB.",
  },
  {
    code: 415,
    name: "Unsupported Media Type",
    category: "4xx",
    summary:
      "The request body's Content-Type is not supported.",
    details:
      "Common when a JSON-only API receives an XML body, or when a multipart upload is missing the boundary parameter.",
  },
  {
    code: 416,
    name: "Range Not Satisfiable",
    category: "4xx",
    summary:
      "The Range header asks for bytes that are out of the resource's range.",
    details:
      "The response should include a Content-Range header showing the resource's total size so the client can recalibrate its request.",
  },
  {
    code: 417,
    name: "Expectation Failed",
    category: "4xx",
    summary:
      "The server cannot meet the requirements in the Expect header.",
    details:
      "Almost always returned for unrecognised expectations, since the only standardised value is 100-continue.",
  },
  {
    code: 418,
    name: "I'm a teapot",
    category: "4xx",
    summary:
      "The Hyper Text Coffee Pot Control Protocol joke — refuses to brew coffee.",
    details:
      "Defined by RFC 2324 (April Fools, 1998). Sometimes used by APIs as a deliberate \"I refuse to honor this request\" signal that is unlikely to collide with anything else.",
  },
  {
    code: 421,
    name: "Misdirected Request",
    category: "4xx",
    summary:
      "The request was sent to a server that cannot produce a response for it.",
    details:
      "Most often seen with HTTP/2 connection coalescing — the client reused a connection that turned out not to authoritatively serve the requested host.",
  },
  {
    code: 422,
    name: "Unprocessable Content",
    category: "4xx",
    summary:
      "The request is well-formed but contains semantic errors.",
    details:
      "Originally a WebDAV status, now widely used by JSON APIs to report validation failures (a field is the wrong type or violates a business rule). Pair with a body that lists the offending fields.",
  },
  {
    code: 423,
    name: "Locked",
    category: "4xx",
    summary:
      "WebDAV — the resource is locked and cannot be modified right now.",
    details:
      "Returned when a WebDAV LOCK is held by another client. The Lock-Token header identifies the active lock.",
  },
  {
    code: 424,
    name: "Failed Dependency",
    category: "4xx",
    summary:
      "WebDAV — the request failed because a previous request it depended on failed.",
    details:
      "Used in multi-step WebDAV operations to indicate a cascading failure. Rarely seen outside WebDAV.",
  },
  {
    code: 425,
    name: "Too Early",
    category: "4xx",
    summary:
      "The server is unwilling to process a request that might be replayed.",
    details:
      "Defined by RFC 8470 for TLS 1.3 0-RTT data. Lets the server reject early requests on endpoints where replay would be unsafe.",
  },
  {
    code: 426,
    name: "Upgrade Required",
    category: "4xx",
    summary:
      "The client must switch to a different protocol given in the Upgrade header.",
    details:
      "Useful when an endpoint requires HTTPS, HTTP/2, or a WebSocket upgrade and refuses to serve plain HTTP/1.1.",
  },
  {
    code: 428,
    name: "Precondition Required",
    category: "4xx",
    summary:
      "The server requires the request to be conditional.",
    details:
      "Defined by RFC 6585. Asks the client to send If-Match or If-Unmodified-Since to avoid lost-update problems on concurrent edits.",
  },
  {
    code: 429,
    name: "Too Many Requests",
    category: "4xx",
    summary: "The client has sent too many requests in a given window.",
    details:
      "Standard rate-limit response. Should include a Retry-After header (in seconds or as an HTTP date) telling the client when it may try again.",
  },
  {
    code: 431,
    name: "Request Header Fields Too Large",
    category: "4xx",
    summary:
      "Individual headers or the header section as a whole are too large.",
    details:
      "Often triggered by oversized cookies or accumulated forwarded headers behind multiple proxies.",
  },
  {
    code: 451,
    name: "Unavailable For Legal Reasons",
    category: "4xx",
    summary:
      "The resource cannot be served because of a legal demand.",
    details:
      "Defined by RFC 7725, named after Fahrenheit 451. The response should describe the legal demand, often via a Link header to the takedown notice.",
  },

  // 5xx — Server Error
  {
    code: 500,
    name: "Internal Server Error",
    category: "5xx",
    summary:
      "A generic error — the server hit an unexpected condition.",
    details:
      "The default \"something blew up\" status. Production servers usually log the underlying error and return a generic body so internal details are not leaked.",
  },
  {
    code: 501,
    name: "Not Implemented",
    category: "5xx",
    summary:
      "The server does not support the functionality required to fulfil the request.",
    details:
      "Use this when an HTTP method is recognised but not yet implemented. 405 is the better choice if the resource itself rejects the method.",
  },
  {
    code: 502,
    name: "Bad Gateway",
    category: "5xx",
    summary:
      "An upstream server returned an invalid response to a gateway or proxy.",
    details:
      "Common when a reverse proxy cannot reach an origin server, or the origin closes the connection mid-response. Distinct from 504 (timeout) and 503 (service unavailable).",
  },
  {
    code: 503,
    name: "Service Unavailable",
    category: "5xx",
    summary:
      "The server is temporarily unable to handle the request.",
    details:
      "Use during maintenance windows, deploys, or when overloaded. A Retry-After header tells the client when to retry.",
  },
  {
    code: 504,
    name: "Gateway Timeout",
    category: "5xx",
    summary:
      "An upstream server did not respond in time.",
    details:
      "Usually means the proxy gave up waiting. Investigate slow upstream queries, timeouts, or saturated thread pools on the origin.",
  },
  {
    code: 505,
    name: "HTTP Version Not Supported",
    category: "5xx",
    summary:
      "The server refuses to support the HTTP protocol version in the request.",
    details:
      "Rarely seen — almost all servers handle HTTP/1.0 and HTTP/1.1. May appear when an old client asks for a version that has been disabled.",
  },
  {
    code: 506,
    name: "Variant Also Negotiates",
    category: "5xx",
    summary:
      "Transparent content negotiation is misconfigured on the server.",
    details:
      "Defined by RFC 2295. Almost never used — transparent content negotiation never gained traction.",
  },
  {
    code: 507,
    name: "Insufficient Storage",
    category: "5xx",
    summary:
      "WebDAV — the server cannot store the representation needed to complete the request.",
    details:
      "Usually disk space. Defined for WebDAV but occasionally reused by upload services to indicate the user has exhausted their quota.",
  },
  {
    code: 508,
    name: "Loop Detected",
    category: "5xx",
    summary:
      "WebDAV — the server detected an infinite loop while processing the request.",
    details:
      "Returned when a WebDAV operation walks into a cycle of bindings that would never terminate.",
  },
  {
    code: 510,
    name: "Not Extended",
    category: "5xx",
    summary:
      "The server requires further extensions to fulfil the request.",
    details:
      "Defined by RFC 2774 for HTTP extension framework, which never saw real-world use.",
  },
  {
    code: 511,
    name: "Network Authentication Required",
    category: "5xx",
    summary:
      "The client must authenticate to gain network access (typically a captive portal).",
    details:
      "Defined by RFC 6585. Lets browsers detect captive portals deterministically instead of guessing from intercepted HTTP responses.",
  },
];
